import { Tray } from '@/components/atoms';
import { TrayScreen } from '@/components/atoms/Tray/interfaces/Tray';
import { ErrorAlert, InfoAlert } from '@/components/molecules';
import UserAssetsList from '@/components/organisms/UserAssetsList/UserAssetsList';
import {
  TrayNavigationProvider,
  useTrayNavigation,
} from '@/components/providers/common/TrayNavigationProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import {
  fetchWithdrawalableTokens,
  withdrawFromWallet,
} from '@/services/WalletsService';
import { TRANSACTIONS, WALLET } from '@/utils/strings';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import WithdrawAssetForm from './WithdrawAssetForm';
import WithdrawAssetFormConfirmationReceipt from './WithdrawAssetFormConfirmationReceipt';
import WithdrawAssetFormConfirmationRequest from './WithdrawAssetFormConfirmationRequest';
const WithdrawFromWalletTrayContent = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}): JSX.Element => {
  const [selectedAsset, setSelectedAsset] =
    useState<WalletTokenInterface | null>(null);
  const [formData, setFormData] = useState<{
    externalWalletAddress: string;
    amount: number;
  } | null>(null);
  const [confirmedWithdrawal, setConfirmedWithdrawal] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { wallets, isUserAuthorized } = useWalletsContext();
  const [withdrawalableTokens, setWithdrawalableTokens] = useState<
    WalletTokenInterface[]
  >([]);
  useEffect(() => {
    const getWithdrawalableTokens = async () => {
      const tokens = await fetchWithdrawalableTokens();
      setWithdrawalableTokens(tokens as unknown as WalletTokenInterface[]);
    };
    getWithdrawalableTokens();
  }, []);
  const { navigateForward, navigateBack } = useTrayNavigation();
  const formRef = useRef<{
    submit: () => void;
    resetForm: () => void;
    setValues: (data: {
      externalWalletAddress: string;
      amount: number;
    }) => void;
  }>(null);
  useEffect(() => {
    if (!isOpen) {
      setSelectedAsset(null);
      setFormData(null);
      setConfirmedWithdrawal(false);
      setLoading(false);
      setErrorMessage(null);
    }
  }, [isOpen]);
  const handleSelectAsset = useCallback(
    (asset: WalletTokenInterface) => {
      setSelectedAsset(asset);
      setErrorMessage(null);
      navigateForward();
      formRef.current?.resetForm();
    },
    [navigateForward],
  );
  const handleNavigateBackToSelectAsset = useCallback(() => {
    setSelectedAsset(null);
    setErrorMessage(null);
    navigateBack();
  }, [navigateBack]);
  const handleNavigateBackToFormSubmission = useCallback(() => {
    formData && formRef.current?.setValues(formData);
    setErrorMessage(null);
    navigateBack();
  }, [formData, navigateBack]);
  const handleFormSubmission = useCallback(
    (data: { externalWalletAddress: string; amount: number }) => {
      setFormData(data);
      setErrorMessage(null);
      navigateForward();
    },
    [navigateForward],
  );
  const handleFormSubmissionConfirmation = useCallback(async () => {
    setConfirmedWithdrawal(true);
    if (formData && selectedAsset) {
      try {
        setLoading(true);
        const wallet = wallets.find(
          (w) => w.address === selectedAsset.walletAddress,
        );
        if (!wallet) {
          throw new Error('Wallet not found.');
        }
        await withdrawFromWallet(
          wallet,
          selectedAsset,
          formData.amount,
          formData.externalWalletAddress,
        );
        navigateForward();
      } catch (error: any) {
        console.error('Withdrawal failed:', error);
        setErrorMessage(`${error.response?.data?.message || error.message}.`);
      } finally {
        setLoading(false);
      }
    }
  }, [formData, selectedAsset, wallets, navigateForward]);
  const screens: TrayScreen[] = [
    {
      title: 'Select an asset',
      content: !isUserAuthorized ? (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      ) : (
        <div className="flex flex-col items-center gap-md">
          <UserAssetsList
            onSelectAsset={handleSelectAsset as any}
            tokens={withdrawalableTokens}
          />
        </div>
      ),
    },
    {
      title: 'Withdraw',
      content: selectedAsset && (
        <WithdrawAssetForm
          selectedAsset={selectedAsset}
          onContinue={handleFormSubmission}
          ref={formRef as any}
        />
      ),
      onNavigateBack: handleNavigateBackToSelectAsset,
      closeButtonLabel: 'Cancel',
      includeFooterContinueButton: true,
      onNavigateForward: () => formRef.current?.submit(),
    },
    {
      title: 'Confirm withdrawal',
      content: formData && selectedAsset && (
        <div className="flex flex-col gap-md">
          <WithdrawAssetFormConfirmationRequest
            data={formData}
            selectedAsset={selectedAsset}
          />
          {errorMessage && (
            <ErrorAlert
              title="Withdrawal was unsuccessful."
              description={errorMessage}
            />
          )}
        </div>
      ),
      onNavigateBack: handleNavigateBackToFormSubmission,
      closeButtonLabel: 'Cancel',
      includeFooterContinueButton: true,
      continueButtonLabel: 'Confirm',
      onNavigateForward: handleFormSubmissionConfirmation,
    },
    {
      title: 'Processing',
      content: formData && selectedAsset && confirmedWithdrawal && (
        <div className="flex flex-col gap-md">
          <WithdrawAssetFormConfirmationReceipt
            data={formData}
            selectedAsset={selectedAsset}
          />
          <InfoAlert
            title={TRANSACTIONS.PROCESSING_TITLE}
            description={TRANSACTIONS.PROCESSING_DESCRIPTION}
          />
        </div>
      ),
      closeButtonLabel: 'Done',
      disableNavigatingBack: true,
    },
  ];
  return (
    <Tray
      isOpen={isOpen}
      onClose={onClose}
      screens={screens}
      loading={loading}
    />
  );
};
const WithdrawFromWalletTray: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = (props) => (
  <TrayNavigationProvider>
    <WithdrawFromWalletTrayContent {...props} />
  </TrayNavigationProvider>
);
export default WithdrawFromWalletTray;
